import { Injectable } from '@angular/core';
import { Observable, concat, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ChatInputMode, ChatItem, ChatQueryRequestPayload, ChatQueryUiResult } from '../../../models/chat-ui.model';
import { ChatService } from '../api/chat.service';
import { ChatQueryUiMapperService } from '../mapping/chat-query-ui-mapper.service';
import { ChatStorageService } from '../session/chat-storage.service';
import { FrontendLoggerService } from '../../frontend-logger.service';
import { ChatIdService } from '../presentation/chat-id.service';
import { ChatCsvFlowService, ChatCsvOpenResult } from './chat-csv-flow.service';
import { PlanLimitError } from '../../../features/plans/models/enforcement.model';
import { PlanLimitErrorService } from '../../../features/plans/services/plan-limit-error.service';

export interface ChatMessageSubmitOptions {
  messageContent: string;
  selectedInputMode: ChatInputMode;
  topK: number;
  limitedDocId: string | null;
  selectedChunkId: string | null;
  /** Slug del rol experto seleccionado (Role-Based Queries). Opcional y backward-compatible. */
  expertRole?: string | null;
  /**
   * Override de `select_mode` de un solo uso (no persiste el modo del chat). Lo usa la acción
   * "Generar Resumen" del menú "+" para pedir una PROPUESTA de generación (`generar_resumen`)
   * sin cambiar el modo activo del usuario.
   */
  overrideSelectMode?: 'generar_resumen';
}

export type ChatMessageFlowEvent =
  | {
      kind: 'user-message';
      message: ChatItem;
    }
  | {
      kind: 'csv-modal';
      result: ChatCsvOpenResult;
    }
  | {
      kind: 'query-result';
      uiResult: ChatQueryUiResult;
    }
  | {
      kind: 'query-error';
      message: ChatItem;
    }
  | {
      kind: 'plan-limit';
      error: PlanLimitError;
    };

@Injectable({
  providedIn: 'root'
})
export class ChatMessageFlowService {
  constructor(
    private chatService: ChatService,
    private queryUiMapper: ChatQueryUiMapperService,
    private chatStorage: ChatStorageService,
    private csvFlow: ChatCsvFlowService,
    private idService: ChatIdService,
    private planLimitErrorService: PlanLimitErrorService,
    private frontendLogger: FrontendLoggerService
  ) {}

  submitMessage(options: ChatMessageSubmitOptions): Observable<ChatMessageFlowEvent> {
    const userMessage: ChatItem = {
      type: 'user',
      content: options.messageContent,
      timestamp: new Date(),
      entityId: this.idService.createMessageEntityId(),
      // Marca por-mensaje del rol con el que se envio la pregunta (solo si hay rol).
      ...(options.expertRole ? { expertRole: options.expertRole } : {})
    };

    // El override (p.ej. "generar_resumen") siempre va por la query normal, nunca por el modal CSV.
    const isCsv = !options.overrideSelectMode && options.selectedInputMode === 'analisis_csv';
    const followUp$ = isCsv
      ? this.csvFlow.openCsvModal(options.messageContent).pipe(
          map(result => ({ kind: 'csv-modal', result }) as ChatMessageFlowEvent)
        )
      : this.submitQuery(options);

    return concat(of({ kind: 'user-message', message: userMessage } as ChatMessageFlowEvent), followUp$);
  }

  private submitQuery(options: ChatMessageSubmitOptions): Observable<ChatMessageFlowEvent> {
    const request = this.buildQueryRequest(options);
    return this.chatService.query(request).pipe(
      map(result => {
        const planLimit = this.planLimitErrorService.fromChatWrapper(result);
        if (planLimit) {
          return {
            kind: 'plan-limit',
            error: planLimit
          } as ChatMessageFlowEvent;
        }
        this.frontendLogger.info(
          'Respuesta recibida del backend para mensaje: ' + options.messageContent,
          'ChatMessageFlowService.submitQuery'
        );
        return {
          kind: 'query-result',
          uiResult: this.queryUiMapper.mapQueryResult(
            result,
            options.messageContent,
            () => this.idService.createMessageEntityId(),
            options.selectedChunkId
          )
        } as ChatMessageFlowEvent;
      }),
      catchError(error => {
        const planLimit = this.planLimitErrorService.fromHttpError(error, 'chat');
        if (planLimit) {
          return of({
            kind: 'plan-limit',
            error: planLimit
          } as ChatMessageFlowEvent);
        }
        this.frontendLogger.error(
          'Error al procesar mensaje de chat: ' + this.formatError(error),
          'ChatMessageFlowService.submitQuery'
        );
        return of({
          kind: 'query-error',
          message: this.buildSystemMessage('Error al procesar mensaje de chat')
        } as ChatMessageFlowEvent);
      })
    );
  }

  private buildQueryRequest(options: ChatMessageSubmitOptions): ChatQueryRequestPayload {
    const sessionId = this.chatStorage.getSessionId();
    const payload: ChatQueryRequestPayload = {
      query: options.messageContent,
      use_llm: true,
      top_k: options.topK,
      selected_mode: options.overrideSelectMode ?? options.selectedInputMode,
      limited_doc_id: options.limitedDocId,
      ...(options.expertRole ? { expert_role: options.expertRole } : {})
    };

    if (!sessionId || sessionId === '0') {
      this.frontendLogger.info(
        'No se encontro sessionId en storage, usando request sin session_id',
        'ChatMessageFlowService.buildQueryRequest'
      );
      return payload;
    }

    return {
      ...payload,
      session_id: sessionId
    };
  }

  private buildSystemMessage(content: string): ChatItem {
    return {
      type: 'system',
      content,
      timestamp: new Date(),
      entityId: this.idService.createMessageEntityId()
    };
  }

  private formatError(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }

    if (error && typeof error === 'object' && 'message' in error) {
      return String((error as { message?: unknown }).message || error);
    }

    return String(error);
  }
}
